import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import {
  seatKey,
  seatCenterInRect,
  flexZonePeople,
  isRowFullyRemoved,
  SEAT_SIZE,
} from '../../lib/arenaUtils';

/**
 * ArenaCanvas - HÁTTÉRKÉP + EXACT SEAT positions.
 * Each sector has explicit seat (x,y) coordinates extracted from the PDF.
 * Hitboxes are pixel-accurate to the seats on the background image.
 */
export default function ArenaCanvas({
  arena,
  removedSeats,
  setRemovedSeats,
  flexZones,
  selectedFlexId,
  setSelectedFlexId,
  updateFlexZone,
  addFlexZoneFromRect,
  stages,
  selectedStageId,
  setSelectedStageId,
  updateStage,
  addStageFromRect,
  selectMode,
  showHitboxes = false,
}) {
  const svgRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [drag, setDrag] = useState(null);
  const [flexDraw, setFlexDraw] = useState(null);
  const [stageDraw, setStageDraw] = useState(null);
  const [pan, setPan] = useState(null);
  const [flexDrag, setFlexDrag] = useState(null);
  const [stageDrag, setStageDrag] = useState(null);
  const [resize, setResize] = useState(null); // {kind:'flex'|'stage', id, handle, startX, startY, x0, y0, w0, h0}
  const [hoverSector, setHoverSector] = useState(null);
  const [hoverRow, setHoverRow] = useState(null); // {sectorId, rowNum}

  const vb = arena.viewBox;

  const toSvgCoords = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    // Use SVG native CTM so we honour preserveAspectRatio letterboxing exactly.
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const sp = pt.matrixTransform(ctm.inverse());
    return { x: sp.x, y: sp.y };
  }, []);

  const viewBoxStr = useMemo(() => {
    const w = vb.width / view.scale;
    const h = vb.height / view.scale;
    return `${view.x} ${view.y} ${w} ${h}`;
  }, [view, vb]);

  const onWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 0.87;
    const newScale = Math.max(0.4, Math.min(8, view.scale * factor));
    const point = toSvgCoords(e.clientX, e.clientY);
    // Cursor relative position within the rendered content area (0..1)
    const rx = (point.x - view.x) / (vb.width / view.scale);
    const ry = (point.y - view.y) / (vb.height / view.scale);
    const newW = vb.width / newScale;
    const newH = vb.height / newScale;
    setView({ x: point.x - rx * newW, y: point.y - ry * newH, scale: newScale });
  };

  const toggleSeat = (sectorId, r, s) => {
    setRemovedSeats((prev) => {
      const set = new Set(prev[sectorId] || []);
      const k = seatKey(r, s);
      if (set.has(k)) set.delete(k); else set.add(k);
      return { ...prev, [sectorId]: Array.from(set) };
    });
  };

  const toggleRow = (sector, rowNum) => {
    const allRemoved = isRowFullyRemoved(removedSeats, sector, rowNum);
    setRemovedSeats((prev) => {
      const set = new Set(prev[sector.id] || []);
      sector.seats.forEach((seat) => {
        if (seat.r === rowNum) {
          const k = seatKey(seat.r, seat.s);
          if (allRemoved) set.delete(k); else set.add(k);
        }
      });
      return { ...prev, [sector.id]: Array.from(set) };
    });
  };

  const toggleSector = (sector) => {
    const total = sector.seats.length;
    const removed = (removedSeats[sector.id] || []).length;
    const allRemoved = removed === total;
    setRemovedSeats((prev) => {
      if (allRemoved) return { ...prev, [sector.id]: [] };
      const all = sector.seats.map((seat) => seatKey(seat.r, seat.s));
      return { ...prev, [sector.id]: all };
    });
  };

  const onMouseDown = (e) => {
    if (e.button === 2 || e.button === 1 || e.shiftKey) {
      // Capture screen-to-logical scale once at the start of pan so the math
      // stays stable while the viewBox is changing during drag.
      const ctm = svgRef.current.getScreenCTM();
      const sx = ctm ? 1 / ctm.a : vb.width / view.scale / svgRef.current.getBoundingClientRect().width;
      const sy = ctm ? 1 / ctm.d : vb.height / view.scale / svgRef.current.getBoundingClientRect().height;
      setPan({ startX: e.clientX, startY: e.clientY, viewX: view.x, viewY: view.y, sx, sy });
      e.preventDefault();
      return;
    }
    if (e.button === 0) {
      const p = toSvgCoords(e.clientX, e.clientY);
      if (selectMode === 'rect') {
        setDrag({ x1: p.x, y1: p.y, x2: p.x, y2: p.y, additive: e.altKey });
      } else if (selectMode === 'flex') {
        setFlexDraw({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
      } else if (selectMode === 'stage') {
        setStageDraw({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
      }
    }
  };

  const onMouseMove = (e) => {
    if (pan) {
      const dx = (e.clientX - pan.startX) * pan.sx;
      const dy = (e.clientY - pan.startY) * pan.sy;
      setView((v) => ({ ...v, x: pan.viewX - dx, y: pan.viewY - dy }));
      return;
    }
    if (drag) {
      const p = toSvgCoords(e.clientX, e.clientY);
      setDrag((d) => ({ ...d, x2: p.x, y2: p.y }));
      return;
    }
    if (flexDraw) {
      const p = toSvgCoords(e.clientX, e.clientY);
      setFlexDraw((d) => ({ ...d, x2: p.x, y2: p.y }));
      return;
    }
    if (stageDraw) {
      const p = toSvgCoords(e.clientX, e.clientY);
      setStageDraw((d) => ({ ...d, x2: p.x, y2: p.y }));
      return;
    }
    if (flexDrag) {
      const p = toSvgCoords(e.clientX, e.clientY);
      updateFlexZone(flexDrag.id, { x: p.x - flexDrag.offsetX, y: p.y - flexDrag.offsetY });
    }
    if (stageDrag) {
      const p = toSvgCoords(e.clientX, e.clientY);
      updateStage(stageDrag.id, { x: p.x - stageDrag.offsetX, y: p.y - stageDrag.offsetY });
    }
    if (resize) {
      const p = toSvgCoords(e.clientX, e.clientY);
      const dx = p.x - resize.startX;
      const dy = p.y - resize.startY;
      const minSize = 20;
      let nx = resize.x0, ny = resize.y0, nw = resize.w0, nh = resize.h0;
      if (resize.handle.includes('e')) nw = Math.max(minSize, resize.w0 + dx);
      if (resize.handle.includes('s')) nh = Math.max(minSize, resize.h0 + dy);
      if (resize.handle.includes('w')) {
        nw = Math.max(minSize, resize.w0 - dx);
        nx = resize.x0 + (resize.w0 - nw);
      }
      if (resize.handle.includes('n')) {
        nh = Math.max(minSize, resize.h0 - dy);
        ny = resize.y0 + (resize.h0 - nh);
      }
      const patch = { x: nx, y: ny, widthPx: nw, heightPx: nh };
      if (resize.kind === 'flex') updateFlexZone(resize.id, patch);
      else updateStage(resize.id, patch);
    }
  };

  const onMouseUp = () => {
    if (pan) { setPan(null); return; }
    if (flexDrag) { setFlexDrag(null); return; }
    if (stageDrag) { setStageDrag(null); return; }
    if (resize) { setResize(null); return; }
    if (flexDraw) {
      const r = {
        x1: Math.min(flexDraw.x1, flexDraw.x2),
        y1: Math.min(flexDraw.y1, flexDraw.y2),
        x2: Math.max(flexDraw.x1, flexDraw.x2),
        y2: Math.max(flexDraw.y1, flexDraw.y2),
      };
      const w = r.x2 - r.x1;
      const h = r.y2 - r.y1;
      if (w > 20 && h > 20 && addFlexZoneFromRect) {
        addFlexZoneFromRect({ x: r.x1, y: r.y1, widthPx: w, heightPx: h });
      }
      setFlexDraw(null);
      return;
    }
    if (stageDraw) {
      const r = {
        x1: Math.min(stageDraw.x1, stageDraw.x2),
        y1: Math.min(stageDraw.y1, stageDraw.y2),
        x2: Math.max(stageDraw.x1, stageDraw.x2),
        y2: Math.max(stageDraw.y1, stageDraw.y2),
      };
      const w = r.x2 - r.x1;
      const h = r.y2 - r.y1;
      if (w > 20 && h > 20 && addStageFromRect) {
        addStageFromRect({ x: r.x1, y: r.y1, widthPx: w, heightPx: h });
      }
      setStageDraw(null);
      return;
    }
    if (drag) {
      const rect = {
        x1: Math.min(drag.x1, drag.x2), y1: Math.min(drag.y1, drag.y2),
        x2: Math.max(drag.x1, drag.x2), y2: Math.max(drag.y1, drag.y2),
      };
      const moved = Math.abs(drag.x2 - drag.x1) > 4 || Math.abs(drag.y2 - drag.y1) > 4;
      if (moved) {
        const action = drag.additive ? 'add' : 'remove';
        setRemovedSeats((prev) => {
          const next = { ...prev };
          arena.sectors.forEach((s) => {
            // Skip sectors whose bbox doesn't intersect the rect for performance
            const b = s.bbox;
            if (b && (b.x + b.width < rect.x1 || b.x > rect.x2 ||
                      b.y + b.height < rect.y1 || b.y > rect.y2)) return;
            const set = new Set(next[s.id] || []);
            (s.seats || []).forEach((seat) => {
              if (seatCenterInRect(seat, rect)) {
                const k = seatKey(seat.r, seat.s);
                if (action === 'remove') set.add(k); else set.delete(k);
              }
            });
            next[s.id] = Array.from(set);
          });
          return next;
        });
      }
      setDrag(null);
    }
  };

  const startFlexDrag = (zone, e) => {
    if (selectMode === 'rect' || selectMode === 'flex' || selectMode === 'stage') return;
    e.stopPropagation();
    const p = toSvgCoords(e.clientX, e.clientY);
    setFlexDrag({ id: zone.id, offsetX: p.x - zone.x, offsetY: p.y - zone.y });
    setSelectedFlexId(zone.id);
  };

  const startStageDrag = (stage, e) => {
    if (selectMode === 'rect' || selectMode === 'flex' || selectMode === 'stage') return;
    e.stopPropagation();
    const p = toSvgCoords(e.clientX, e.clientY);
    setStageDrag({ id: stage.id, offsetX: p.x - stage.x, offsetY: p.y - stage.y });
    setSelectedStageId(stage.id);
  };

  const startResize = (kind, item, handle, e) => {
    e.stopPropagation();
    const p = toSvgCoords(e.clientX, e.clientY);
    setResize({
      kind, id: item.id, handle,
      startX: p.x, startY: p.y,
      x0: item.x, y0: item.y, w0: item.widthPx, h0: item.heightPx,
    });
    if (kind === 'flex') setSelectedFlexId(item.id);
    else setSelectedStageId(item.id);
  };

  const resetView = () => setView({ x: 0, y: 0, scale: 1 });

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setDrag(null); setFlexDraw(null); setStageDraw(null); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const cursor = pan ? 'grabbing'
    : selectMode === 'rect' ? 'crosshair'
    : selectMode === 'flex' ? 'cell'
    : selectMode === 'stage' ? 'cell'
    : selectMode === 'edit' ? 'move'
    : 'pointer';

  return (
    <div className="relative w-full h-full bg-stone-50 overflow-hidden" data-testid="arena-canvas-wrapper">
      <button
        onClick={resetView}
        data-testid="reset-zoom-btn"
        className="absolute top-3 right-3 z-10 px-3 py-1.5 text-xs font-medium tracking-wide bg-white border border-stone-300 hover:bg-stone-100 rounded-full shadow-sm transition-colors"
      >
        Nézet vissza
      </button>
      <div className="absolute bottom-3 left-3 z-10 text-[11px] bg-white/90 backdrop-blur px-3 py-2 rounded-md border border-stone-200 text-stone-600 leading-relaxed max-w-[340px]">
        <div className="font-semibold text-stone-800 mb-0.5">Használat</div>
        <div>• <b>Kattintás módban</b>: kattints egy székre / szektor cimkére</div>
        <div>• <b>Téglalap módban</b>: bal-egér drag → tömeges kivétel (Alt = vissza)</div>
        <div>• <b>Flex rajz módban</b>: drag → új flex zóna; a méteret utólag a panelben adod meg</div>
        <div>• <b>Színpad rajz módban</b>: drag → új színpad; nem számít a kapacitásba</div>
        <div>• <b>Szerkesztés módban</b>: kattints flex / színpad blokkra; sarokpontokkal méretezhetõ, középrõl húzva mozgatható</div>
        <div>• Mozgatás: Shift + drag • Zoom: egérgörgő</div>
      </div>

      <svg
        ref={svgRef}
        viewBox={viewBoxStr}
        className="w-full h-full select-none touch-none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onContextMenu={(e) => e.preventDefault()}
        style={{ cursor }}
        data-testid="arena-svg"
      >
        {/* Háttér: REFERENCIA KÉP - tűpontos vizuál */}
        <rect x="0" y="0" width={vb.width} height={vb.height} fill="white" />
        {arena.bgImage && (
          <image
            href={arena.bgImage}
            x="0"
            y="0"
            width={vb.width}
            height={vb.height}
            preserveAspectRatio="xMidYMid meet"
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Per-sector seat overlays */}
        {arena.sectors.map((s) => (
          <SectorOverlay
            key={s.id}
            sector={s}
            removedSet={new Set(removedSeats[s.id] || [])}
            onSeatClick={(r, sn) => toggleSeat(s.id, r, sn)}
            onRowClick={(r) => toggleRow(s, r)}
            onSectorClick={() => toggleSector(s)}
            isHover={hoverSector === s.id}
            onHover={() => setHoverSector(s.id)}
            onLeave={() => setHoverSector(null)}
            hoverRow={hoverRow && hoverRow.sectorId === s.id ? hoverRow.rowNum : null}
            setHoverRow={(rowNum) => setHoverRow(rowNum == null ? null : { sectorId: s.id, rowNum })}
            showHitboxes={showHitboxes}
            zoom={view.scale}
            disabled={selectMode === 'edit'}
          />
        ))}

        {/* Flex zónák */}
        {flexZones.map((z) => (
          <FlexZoneShape
            key={z.id}
            zone={z}
            selected={z.id === selectedFlexId}
            onMouseDown={(e) => startFlexDrag(z, e)}
            onClick={() => setSelectedFlexId(z.id)}
            editMode={selectMode === 'edit'}
            onResizeStart={(handle, e) => startResize('flex', z, handle, e)}
          />
        ))}

        {/* Színpadok */}
        {(stages || []).map((s) => (
          <StageShape
            key={s.id}
            stage={s}
            selected={s.id === selectedStageId}
            onMouseDown={(e) => startStageDrag(s, e)}
            onClick={() => setSelectedStageId(s.id)}
            editMode={selectMode === 'edit'}
            onResizeStart={(handle, e) => startResize('stage', s, handle, e)}
          />
        ))}

        {/* Drag rect (rect select) */}
        {drag && (
          <rect
            x={Math.min(drag.x1, drag.x2)}
            y={Math.min(drag.y1, drag.y2)}
            width={Math.abs(drag.x2 - drag.x1)}
            height={Math.abs(drag.y2 - drag.y1)}
            fill={drag.additive ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)'}
            stroke={drag.additive ? '#22c55e' : '#ef4444'}
            strokeWidth="1.5"
            strokeDasharray="5,3"
            pointerEvents="none"
          />
        )}

        {/* Flex draw rect */}
        {flexDraw && (
          <rect
            x={Math.min(flexDraw.x1, flexDraw.x2)}
            y={Math.min(flexDraw.y1, flexDraw.y2)}
            width={Math.abs(flexDraw.x2 - flexDraw.x1)}
            height={Math.abs(flexDraw.y2 - flexDraw.y1)}
            fill="rgba(245,158,11,0.22)"
            stroke="#d97706"
            strokeWidth="2"
            strokeDasharray="6,4"
            pointerEvents="none"
          />
        )}

        {/* Stage draw rect */}
        {stageDraw && (
          <rect
            x={Math.min(stageDraw.x1, stageDraw.x2)}
            y={Math.min(stageDraw.y1, stageDraw.y2)}
            width={Math.abs(stageDraw.x2 - stageDraw.x1)}
            height={Math.abs(stageDraw.y2 - stageDraw.y1)}
            fill="rgba(51,65,85,0.30)"
            stroke="#1e293b"
            strokeWidth="2"
            strokeDasharray="6,4"
            pointerEvents="none"
          />
        )}
      </svg>
    </div>
  );
}

// ============= Szektor OVERLAY (per-seat hitboxes) =============
const SectorOverlay = ({
  sector, removedSet, onSeatClick, onRowClick, onSectorClick,
  isHover, onHover, onLeave, hoverRow, setHoverRow, showHitboxes, disabled,
}) => {
  const total = sector.seats?.length || 0;
  const removed = removedSet.size;
  const isAllRemoved = removed === total && total > 0;

  // Group seats by row for row strip rendering
  const rowMap = useMemo(() => {
    const m = {};
    (sector.seats || []).forEach((seat) => {
      if (!m[seat.r]) m[seat.r] = [];
      m[seat.r].push(seat);
    });
    return m;
  }, [sector.seats]);

  return (
    <g
      data-testid={`sector-group-${sector.id}`}
      onMouseEnter={disabled ? undefined : onHover}
      onMouseLeave={disabled ? undefined : () => { onLeave(); setHoverRow(null); }}
      style={disabled ? { pointerEvents: 'none' } : undefined}
    >
      {/* Egész szektor click overlay - HÁTTÉRBEN */}
      <rect
        x={sector.bbox.x}
        y={sector.bbox.y}
        width={sector.bbox.width}
        height={sector.bbox.height}
        fill={isAllRemoved ? 'rgba(220,38,38,0.55)' : 'rgba(0,0,0,0)'}
        rx="2"
        onClick={(e) => { e.stopPropagation(); onSectorClick(); }}
        style={{ cursor: 'pointer' }}
        data-testid={`sector-header-${sector.id}`}
      />

      {/* Sor szintű strip overlay - kattintható, csak hover-rel látható */}
      {!isAllRemoved && Object.entries(rowMap).map(([rowNum, rowSeats]) => {
        const rn = parseInt(rowNum);
        const xs = rowSeats.map((s) => s.x);
        const ys = rowSeats.map((s) => s.y);
        const x0 = Math.min(...xs);
        const x1 = Math.max(...xs) + SEAT_SIZE;
        const y0 = Math.min(...ys);
        const y1 = Math.max(...ys) + SEAT_SIZE;
        const isRowHover = hoverRow === rn;
        return (
          <rect
            key={`row-${rn}`}
            x={x0}
            y={y0}
            width={x1 - x0}
            height={y1 - y0}
            fill={isRowHover ? 'rgba(59,130,246,0.25)' : 'rgba(0,0,0,0)'}
            stroke={isRowHover ? '#1d4ed8' : 'transparent'}
            strokeWidth="0.5"
            onMouseEnter={() => setHoverRow(rn)}
            onMouseLeave={() => setHoverRow(null)}
            onClick={(e) => { e.stopPropagation(); onRowClick(rn); }}
            style={{ cursor: 'pointer' }}
            data-testid={`row-strip-${sector.id}-${rn}`}
          />
        );
      })}

      {/* PER-SEAT hitboxes - láthatatlan, kivéve hitbox debug mód */}
      {!isAllRemoved && (sector.seats || []).map((seat) => {
        const k = seatKey(seat.r, seat.s);
        const isRemoved = removedSet.has(k);
        return (
          <rect
            key={`s-${seat.r}-${seat.s}`}
            x={seat.x}
            y={seat.y}
            width={SEAT_SIZE}
            height={SEAT_SIZE}
            fill={isRemoved ? '#dc2626' : (showHitboxes ? 'rgba(34,197,94,0.30)' : 'rgba(0,0,0,0)')}
            stroke={showHitboxes ? '#16a34a' : 'transparent'}
            strokeWidth="0.3"
            opacity={isRemoved ? 0.85 : 1}
            onClick={(e) => { e.stopPropagation(); onSeatClick(seat.r, seat.s); }}
            style={{ cursor: 'pointer' }}
            data-testid={`seat-${sector.id}-${seat.r}-${seat.s}`}
          />
        );
      })}

      {/* Hover esetén szektor cím */}
      {isHover && (
        <g pointerEvents="none">
          <rect
            x={sector.bbox.x}
            y={sector.bbox.y - 22}
            width={Math.max(sector.bbox.width, 90)}
            height={18}
            fill="#1e40af"
            rx="2"
          />
          <text
            x={sector.bbox.x + Math.max(sector.bbox.width, 90) / 2}
            y={sector.bbox.y - 10}
            fill="white"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            style={{ letterSpacing: '0.5px' }}
          >
            {sector.name} • {total - removed}/{total}
          </text>
        </g>
      )}
    </g>
  );
};

// ============= Resize handle-ek (8 db: sarkok + élközepek) =============
const ResizeHandles = ({ x, y, w, h, color = '#0ea5e9', onResizeStart }) => {
  const SIZE = 10;
  const handles = [
    { id: 'nw', cx: x, cy: y, cur: 'nwse-resize' },
    { id: 'n',  cx: x + w/2, cy: y, cur: 'ns-resize' },
    { id: 'ne', cx: x + w, cy: y, cur: 'nesw-resize' },
    { id: 'e',  cx: x + w, cy: y + h/2, cur: 'ew-resize' },
    { id: 'se', cx: x + w, cy: y + h, cur: 'nwse-resize' },
    { id: 's',  cx: x + w/2, cy: y + h, cur: 'ns-resize' },
    { id: 'sw', cx: x, cy: y + h, cur: 'nesw-resize' },
    { id: 'w',  cx: x, cy: y + h/2, cur: 'ew-resize' },
  ];
  return (
    <g pointerEvents="all">
      {handles.map((h) => (
        <rect
          key={h.id}
          x={h.cx - SIZE/2}
          y={h.cy - SIZE/2}
          width={SIZE}
          height={SIZE}
          fill="white"
          stroke={color}
          strokeWidth="2"
          style={{ cursor: h.cur }}
          onMouseDown={(e) => onResizeStart(h.id, e)}
          data-testid={`resize-handle-${h.id}`}
        />
      ))}
    </g>
  );
};

// ============= Flex zóna alakzat =============
const FlexZoneShape = ({ zone, selected, onMouseDown, onClick, editMode, onResizeStart }) => {
  const people = flexZonePeople(zone);
  return (
    <g
      data-testid={`flex-zone-${zone.id}`}
    >
      <g onMouseDown={onMouseDown} onClick={onClick} style={{ cursor: 'move' }}>
        <rect
          x={zone.x}
          y={zone.y}
          width={zone.widthPx}
          height={zone.heightPx}
          fill="rgba(245,158,11,0.45)"
          stroke={selected ? '#b45309' : '#d97706'}
          strokeWidth={selected ? '3' : '2'}
          strokeDasharray="6,4"
          rx="4"
        />
        <text
          x={zone.x + zone.widthPx / 2}
          y={zone.y + zone.heightPx / 2 - 8}
          textAnchor="middle"
          fill="#78350f"
          fontSize="14"
          fontWeight="700"
          style={{ pointerEvents: 'none' }}
        >
          {zone.name}
        </text>
        <text
          x={zone.x + zone.widthPx / 2}
          y={zone.y + zone.heightPx / 2 + 10}
          textAnchor="middle"
          fill="#92400e"
          fontSize="12"
          fontWeight="600"
          style={{ pointerEvents: 'none' }}
        >
          {zone.widthM}×{zone.heightM} m • {people} fő
        </text>
      </g>
      {editMode && selected && (
        <ResizeHandles
          x={zone.x}
          y={zone.y}
          w={zone.widthPx}
          h={zone.heightPx}
          color="#d97706"
          onResizeStart={onResizeStart}
        />
      )}
    </g>
  );
};

// ============= Színpad alakzat =============
const StageShape = ({ stage, selected, onMouseDown, onClick, editMode, onResizeStart }) => {
  return (
    <g
      data-testid={`stage-${stage.id}`}
    >
      <g onMouseDown={onMouseDown} onClick={onClick} style={{ cursor: 'move' }}>
        <rect
          x={stage.x}
          y={stage.y}
          width={stage.widthPx}
          height={stage.heightPx}
          fill="rgba(51,65,85,0.85)"
          stroke={selected ? '#0ea5e9' : '#1e293b'}
          strokeWidth={selected ? '3' : '1.5'}
          rx="3"
        />
        <text
          x={stage.x + stage.widthPx / 2}
          y={stage.y + stage.heightPx / 2 - 6}
          textAnchor="middle"
          fill="white"
          fontSize="15"
          fontWeight="800"
          style={{ pointerEvents: 'none', letterSpacing: '1px' }}
        >
          {stage.name}
        </text>
        <text
          x={stage.x + stage.widthPx / 2}
          y={stage.y + stage.heightPx / 2 + 12}
          textAnchor="middle"
          fill="#cbd5e1"
          fontSize="12"
          fontWeight="600"
          style={{ pointerEvents: 'none' }}
        >
          {stage.widthM}×{stage.heightM} m
        </text>
      </g>
      {editMode && selected && (
        <ResizeHandles
          x={stage.x}
          y={stage.y}
          w={stage.widthPx}
          h={stage.heightPx}
          color="#0ea5e9"
          onResizeStart={onResizeStart}
        />
      )}
    </g>
  );
};

